/**
 * The region a page is rendered for, from the path (/au, /uk, /ca). Facts come from two config
 * files and one copy package, never from a map in this app: words and prices from
 * `web_regions.yaml`, which layers are on from `jurisdiction.yaml`, fixed sentences from
 * packages/copy.
 */
import { useParams } from "react-router";
import { webRegion, acceptedPrice, jurisdictionForMarket, jurisdictionPolicy, webRegionsConfig, type WebRegion, type JurisdictionPolicy } from "@oc/config";
import { CURRENCY_SYMBOL, ADVICE_LINE, ADVICE_HEADING, ADVICE_BODY } from "@oc/copy";

export interface Region {
  code: string;
  web: WebRegion;
  policy: JurisdictionPolicy;
  symbol: string;
  /** The region's fixed disclaimers (packages/copy web.ts) — the words a page may print with "advice" in them. */
  advice: { line: string; heading: string; body: string };
  /** A formatted accepted price, or null — the caller prints a placeholder (Design System §18). */
  price: (key: string) => string | null;
  href: (path: string) => string;
}

export const isLive = (code: string | undefined): code is string => code !== undefined && webRegionsConfig.live.includes(code as never);

export function regionFor(code: string): Region {
  const web = webRegion(code);
  const symbol = CURRENCY_SYMBOL[web.currency]!;
  return {
    code, web, policy: jurisdictionPolicy(jurisdictionForMarket(code)), symbol,
    advice: { line: ADVICE_LINE[code as keyof typeof ADVICE_LINE], heading: ADVICE_HEADING[code as keyof typeof ADVICE_HEADING], body: ADVICE_BODY[code as keyof typeof ADVICE_BODY] },
    price: (key) => { const n = acceptedPrice(code, key); return n === null ? null : `${symbol}${n.toLocaleString("en-AU")}`; },
    href: (path) => `/${code}${path.startsWith("/") ? path : `/${path}`}`.replace(/\/$/, "") || `/${code}`,
  };
}

export function useRegion(): Region {
  const { region } = useParams();
  return regionFor(isLive(region) ? region : "au");
}

export const FLAG_LABELS: Record<string, string> = Object.fromEntries(Object.entries(webRegionsConfig.regions).map(([k, v]) => [k, v.name]));
